/*  Copyright 2022 Sebastian Spautz

    This File is Part of "RPGSheetManager - Forbidden Lands".

    "RPGSheetManager" is free software: you can redistribute it and/or modify
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    any later version.
    This program is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.
    You should have received a copy of the GNU General Public License
    along with this program.  If not, see http://www.gnu.org/licenses/.
*/
import CollaborationControls from '../../../../node_modules/@sebastiansit/formoperator/src/main/scripts/components/collaborationControls.mjs'

/** Gets a text in german or english for the data protecton hint.
 *
 * @returns {string} Translated markup for data protection hint
 */
function dataProtectionHint () {
  if (document.documentElement.lang.startsWith('de')) {
    return `<p>Beachte bitte, wenn du diese Funktion zum gemeinsamen Bearbeiten nutzt, werden 
      Daten, über die du identifiziert werden könntest, an 
      <a href="https://peerjs.com/">PeerJS</a> übermittelt.
      Dies geschieht um die Verbindung zwischen dir und deinen Mitspielern 
      herzustellen.</p>
      <p>Wenn du das nicht möchtest, darfst du die Funktion zum gemeinsamen Bearbeiten
      nicht mit den unten stehenden Schaltflächen aktivieren.</p>`
  } else {
    return `<p>Please note: When you use this collaborative editing feature, data that could 
      identify you is transmitted to <a href="https://peerjs.com/">PeerJS</a>.</p>
      <p>If you don't agree with this, you must not activate the collaborative editing 
      function with the buttons below.</p>`
  }
}
/** Gets a text in german or english for the nickname intro.
 *
 * @returns {string} Translated markup
 */
function nicknameIntro () {
  if (document.documentElement.lang.startsWith('de')) {
    return 'Wie willst du heute genannt werden?'
  } else {
    return 'What do you like to be called?'
  }
}
/** Gets a text in german or english for the nickname label.
 *
 * @returns {string} Translated markup
 */
function labelNickname () {
  if (document.documentElement.lang.startsWith('de')) {
    return 'Dein Name'
  } else {
    return 'Nickname'
  }
}
/** Gets a text in german or english for the host intro.
 *
 * @returns {string} Translated markup
 */
function hostIntro () {
  if (document.documentElement.lang.startsWith('de')) {
    return 'Willst du dieses Formular gemeinsam bearbeiten?'
  } else {
    return 'Do you want to start collaborative editing?'
  }
}
/** Gets a text in german or english for the button Start.
 *
 * @returns {string} Translated markup
 */
function buttonStart () {
  if (document.documentElement.lang.startsWith('de')) {
    return 'Zusammenarbeit erlauben'
  } else {
    return 'Start Collaborating'
  }
}
/** Gets a text in german or english for the connect intro.
 *
 * @returns {string} Translated markup
 */
function connectIntro () {
  if (document.documentElement.lang.startsWith('de')) {
    return 'Willst du eine Verbindung zum Formular einer anderen Person herstellen?'
  } else {
    return 'Do you want to connect to another persons form?'
  }
}
/** Gets a text in german or english for the button Connect.
 *
 * @returns {string} Translated markup
 */
function buttonConnect () {
  if (document.documentElement.lang.startsWith('de')) {
    return 'Verbinden'
  } else {
    return 'Connect'
  }
}
/** Gets a text in german or english for the description text about the host id.
 *
 * @returns {string} Translated markup
 */
function hostedGameNumber () {
  if (document.documentElement.lang.startsWith('de')) {
    return 'Deine Freunde brauchen die folgende ID für eine Verbindung'
  } else {
    return 'Your friends need the following ID to connect'
  }
}

const template = document.createElement('template')
template.innerHTML = `
  <style>
    p {
      max-width: 50em;
      line-height: 1.5em;
    }
    p:first-child {
        margin-block-start: 0;
    }
    p:last-child {
        margin-block-end: 0;
    }

    dialog {
      max-width: 90vw;
      padding: 0;
      box-shadow: -2rem 2rem 2rem 0.5rem rgba(0 0 0 / 75%);
    }
    dialog > * {
      padding: 1rem;
    }
    dialog::backdrop {
      background: rgba(0 0 0 / 50%);
    }

    dialog header {
      display: flex;
      align-items: center;
      background-color: #006400;
      padding: 1rem;
      color: whitesmoke;
      border-block-end: var(--small-margin) dashed #004600;
    }

    dialog header h2 {
      flex-grow: 1;
      margin: 0;
    }

    button {
      font-size: inherit;
    }
  </style>

  <button type="button" id="control_collaborate">Collaborate</button>
  <dialog>
    <header>
      <h2>Collaborate</h2>
      <button id="control_closeDialog" type="button">Close</button>
    </header>
    <form action="dialog">
      <sit-collaborationcontrols>
        <div slot="dataProtectionHint">${dataProtectionHint()}</div>
        <span slot="nicknameIntro">${nicknameIntro()}</span>
        <span slot="nickname">${labelNickname()}</span>
        <span slot="hostIntro">${hostIntro()}</span>
        <span slot="buttonStart">${buttonStart()}</span>
        <span slot="hostedGameNumber">${hostedGameNumber()}</span>
        <span slot="connectIntro">${connectIntro()}</span>
        <span slot="buttonConnect">${buttonConnect()}</span>
      </sit-collaborationcontrols>
    </form>
  </dialog>
`

window.customElements.define('sit-collaborationcontrols', CollaborationControls)

/** A Custom Element that allows to open a config dialot.
 * This dialog allows to set a user name and host a WebRTC-Channel or connect
 * to such a channel as a client.
 *
 * @class
 * @augments {external:HTMLElement}
 */
class CollaborationConfig extends HTMLElement {
  /** Constructor for the HTMLElement "CollaborationControls".
   */
  constructor () {
    super()

    this._shadowRoot = this.attachShadow({ mode: 'open' })
    this._shadowRoot.appendChild(template.content.cloneNode(true))

    const controlButton = this._shadowRoot.getElementById('control_collaborate')
    const onControlButton = (event) => {
      const collaborationDialog = this._shadowRoot.querySelector('dialog')
      if (collaborationDialog.open) {
        collaborationDialog.close()
      } else {
        collaborationDialog.showModal()
      }
    }
    controlButton.addEventListener('pointerup', onControlButton)
    controlButton.addEventListener('keyup', (event) => {
      if (event.key === 'Enter') {
        onControlButton(event)
      }
    })

    const controlCloseDialog = this._shadowRoot.getElementById('control_closeDialog')
    const onCloseDialog = (event) => {
      const collaborationDialog = this._shadowRoot.querySelector('dialog')
      collaborationDialog.close()
    }
    controlCloseDialog.addEventListener('pointerup', onCloseDialog)
    controlCloseDialog.addEventListener('keyup', (event) => {
      if (event.key === 'Enter') {
        onCloseDialog(event)
      }
    })
  }

  /** The &lt;form&gt; element to associate the controls with (there form owner).
   *
   * @returns {external:HTMLFormElement} A &lt;form&gt; element.
   */
  get form () {
    return this.getAttribute('form')
  }

  /** Set the ID of the associated form element.
   *
   * @param {string} value The new label.
   */
  set form (value) {
    this.setAttribute('form', value)
  }

  /** Get the label of the button "collaborate".
   *
   * @returns {string} The label.
   */
  get labelCollaborate () {
    return this.getAttribute('label-collaborate')
  }

  /** Set the label for the button "collaborate".
   *
   * @param {string} value The new label.
   */
  set labelCollaborate (value) {
    this.setAttribute('label-collaborate', value)
  }

  /** Get the label of the button "close".
   *
   * @returns {string} The label.
   */
  get labelClose () {
    return this.getAttribute('label-close')
  }

  /** Set the label for the button "close".
   *
   * @param {string} value The new label.
   */
  set labelClose (value) {
    this.setAttribute('label-close', value)
  }

  /** Declares all HTML attributes that should observed for changes.
   *
   * @private
   * @returns {string[]} Array of attribute names.
   */
  static get observedAttributes () {
    return ['form', 'label-collaborate', 'label-close']
  }

  /** This Method computes all changes of HTML attribute values.
   *
   * @private
   * @param {string} name The name of the changed attribute.
   * @param {string} oldVal The old value of the attribute.
   * @param {string} newVal The new value of the attribute.
   * @returns {undefined}
   */
  attributeChangedCallback (name, oldVal, newVal) {
    switch (name) {
      case 'form':
        this._shadowRoot.querySelector('sit-collaborationcontrols').form = newVal
        break
      case 'label-collaborate':
        this._shadowRoot.querySelector('#control_collaborate').innerHTML = newVal
        this._shadowRoot.querySelector('h2').innerHTML = newVal
        break
      case 'label-close':
        this._shadowRoot.querySelector('#control_closeDialog').innerHTML = newVal
        break
      default:
        this[name] = newVal
    }
  }
}

export default CollaborationConfig
